/**
 * SeasonContext - holds:
 *   - actingAsTeamId: the LeagueTeam id this tester is currently acting as
 *     (read from localStorage; persists across reloads)
 *
 * Used by the dev-flag-gated Team Switcher. Defaults to null (real auth user).
 */
import { createContext, useContext, useState, useCallback, useEffect } from "react";

const SeasonContext = createContext(null);
const STORAGE_KEY = "pgf_acting_team_id";

export function SeasonProvider({ children }) {
  const [actingAsTeamId, setActingAsTeamIdRaw] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  const setActingAsTeamId = useCallback((id) => {
    setActingAsTeamIdRaw(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setActingAsTeamIdRaw(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <SeasonContext.Provider value={{ actingAsTeamId, setActingAsTeamId }}>
      {children}
    </SeasonContext.Provider>
  );
}

export const useSeason = () => useContext(SeasonContext) || { actingAsTeamId: null, setActingAsTeamId: () => {} };

export const isTeamSwitcherEnabled = () => {
  // Gated. In dev: REACT_APP_DEV_TEAM_SWITCH=true. Off in production builds.
  return process.env.REACT_APP_DEV_TEAM_SWITCH === "true" || process.env.NODE_ENV === "development";
};
